"use client"

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react"

export type ParentApp = "FBO" | "Portal"

type ParentAppContextType = {
  parentApp: ParentApp
  setParentApp: (value: ParentApp) => void
}

const VALID_VALUES: ParentApp[] = ["FBO", "Portal"]
const DEFAULT_PARENT_APP: ParentApp = "FBO"

const ParentAppContext = createContext<ParentAppContextType | undefined>(undefined)

export function ParentAppProvider({ children }: { children: ReactNode }) {
  const [parentApp, setParentAppState] = useState<ParentApp>(DEFAULT_PARENT_APP)

  useEffect(() => {
    if (typeof window === "undefined") return
    const params = new URLSearchParams(window.location.search)
    const fromUrl = params.get("parentApp")
    if (fromUrl && VALID_VALUES.includes(fromUrl as ParentApp)) {
      setParentAppState(fromUrl as ParentApp)
    } else {
      params.set("parentApp", DEFAULT_PARENT_APP)
      const newUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`
      window.history.replaceState({}, "", newUrl)
    }
  }, [])

  useEffect(() => {
    const onPopState = () => {
      if (typeof window === "undefined") return
      const params = new URLSearchParams(window.location.search)
      const fromUrl = params.get("parentApp")
      if (fromUrl && VALID_VALUES.includes(fromUrl as ParentApp)) {
        if (fromUrl !== parentApp) {
          setParentAppState(fromUrl as ParentApp)
        }
      } else {
        setParentAppState(DEFAULT_PARENT_APP)
        params.set("parentApp", DEFAULT_PARENT_APP)
        const newUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`
        window.history.replaceState({}, "", newUrl)
      }
    }
    window.addEventListener("popstate", onPopState)
    return () => window.removeEventListener("popstate", onPopState)
  }, [parentApp])

  const setParentApp = (value: ParentApp) => {
    setParentAppState(value)
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search)
      params.set("parentApp", value)
      const newUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`
      window.history.replaceState({}, "", newUrl)
    }
  }

  const contextValue = useMemo(() => ({ parentApp, setParentApp }), [parentApp])

  return (
    <ParentAppContext.Provider value={contextValue}>{children}</ParentAppContext.Provider>
  )
}

export function useParentApp() {
  const ctx = useContext(ParentAppContext)
  if (!ctx) throw new Error("useParentApp must be used within a ParentAppProvider")
  return ctx
}
